/**
 * lg-brand-marquee-a11y — accessibility enhancement for <marquee-component>.
 *
 * WHY THIS EXISTS
 * Horizon's assets/marquee.js fills the track by cloning, and both clone paths
 * create accessibility defects as soon as a marquee item contains a link
 * (Horizon's own _marquee blocks are non-interactive, so upstream never hits this):
 *
 *   1. #duplicateContent() clones `.marquee__content` and sets aria-hidden="true"
 *      on the clone — but the cloned <a>s stay in the tab order. A focusable element
 *      inside an aria-hidden subtree is axe `aria-hidden-focus` / WCAG 4.1.2.
 *   2. #addRepeatedItems() clones marqueeItems[0] WITHOUT aria-hidden, so every
 *      link is announced and tabbable N times over.
 *
 * Neither is fixable in CSS (nothing removes an element from the tab order from a
 * stylesheet), and we do not edit vendored Horizon files. This scopes the fix to
 * LG marquees only and leaves marquee.js untouched.
 *
 * WHAT IT DOES
 *   - Marks every clone `inert` (removes it from the a11y tree AND the tab order,
 *     with an aria-hidden + tabindex fallback for browsers without inert).
 *     Only the ORIGINAL marqueeItems[0] stays interactive.
 *   - Adds keyboard pause/resume on focusin/focusout, mirroring marquee.js's own
 *     pointerenter/pointerleave handling. Without it a keyboard user tabbing into a
 *     moving strip gets a target that keeps translating — WCAG 2.2.2.
 *
 * Progressive enhancement: if this never runs the marquee still works, it is just
 * the accessibility that degrades. Idempotent; the element-scoped observer also handles resize clones.
 */
(function () {
  var SEL = ".lg-brand-marquee marquee-component";

  function deactivate(node, isOriginal) {
    if (!node || node.nodeType !== 1 || isOriginal) return;
    if (node.dataset.lgMarqueeInert === "") return;
    node.dataset.lgMarqueeInert = "";
    // `inert` covers focus + a11y tree in supporting browsers.
    node.inert = true;
    if (!("inert" in HTMLElement.prototype)) {
      node.setAttribute("aria-hidden", "true");
      var focusable = node.querySelectorAll("a[href], button, [tabindex]");
      for (var i = 0; i < focusable.length; i++) focusable[i].setAttribute("tabindex", "-1");
    }
  }

  function settle(root) {
    var content = root.querySelector(".marquee__content");
    if (!content) return false;
    // Any duplicated `.marquee__content` past the first is a #duplicateContent() clone.
    var contents = root.querySelectorAll(".marquee__content");
    for (var i = 1; i < contents.length; i++) deactivate(contents[i], false);
    // Within the original content, marqueeItems[0] is authored; the rest are
    // #addRepeatedItems() clones.
    var items = content.querySelectorAll(".marquee__repeated-items");
    for (var j = 1; j < items.length; j++) deactivate(items[j], false);
    return contents.length > 1 || items.length > 1;
  }

  function pauseOnKeyboardFocus(root) {
    if (root.dataset.lgMarqueeKb === "") return;
    root.dataset.lgMarqueeKb = "";
    var wrapper = root.querySelector(".marquee__wrapper");
    if (!wrapper) return;
    var set = function (rate) {
      var anims = wrapper.getAnimations ? wrapper.getAnimations() : [];
      for (var i = 0; i < anims.length; i++) anims[i].playbackRate = rate;
    };
    root.addEventListener("focusin", function () {
      set(0);
    });
    root.addEventListener("focusout", function (e) {
      if (!root.contains(e.relatedTarget)) set(1);
    });
  }

  function enhance(root) {
    var button = root.closest(".lg-brand-marquee").querySelector("[data-marquee-pause]");
    if (button && !button.dataset.bound) {
      button.dataset.bound = "true";
      button.hidden = false;
      button.addEventListener("click", function () {
        var paused = root.toggleAttribute("data-user-paused");
        button.setAttribute("aria-pressed", String(paused));
      });
    }
    pauseOnKeyboardFocus(root);
    if (settle(root)) return true;
    return false;
  }

  function init() {
    var roots = document.querySelectorAll(SEL);
    for (var i = 0; i < roots.length; i++) {
      (function (root) {
        enhance(root);
        if (root.dataset.lgMarqueeObserved === "") return;
        root.dataset.lgMarqueeObserved = "";
        // Resize may replace the cloned sets; keep this observer local to the element.
        var obs = new MutationObserver(function () {
          enhance(root);
        });
        obs.observe(root, { childList: true, subtree: true });
      })(roots[i]);
    }
  }

  document.addEventListener("shopify:section:load", init);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
